// notificationsStore.ts
import { create } from "zustand";
import type { CustomAlertProps } from "../../components/base/CustomAlert";
import { getError } from "../../utils/getError";
import type { TFunction } from "i18next";
import type { UseFormReset } from "react-hook-form";
import api, { apiFormData, isAbortError } from "../../utils/api";
import { toast } from "sonner";
import { getAdminFetchGeneration } from "../../utils/adminFetchGeneration";

export interface Notification {
  id: string;
  createdBy: string;
  title: string;
  description: string;
  type_of_addresse: string;
  addressee: string;
  addresses?: string[];
  priority: string;
  icon: string;
  icon_id?: string;
  image: string;
  schedule_shipment?: string;
  status: string;
  status_id: string;
  createdAt: { _seconds: number; _nanoseconds: number };
  updatedAt: { _seconds: number; _nanoseconds: number };
  collections: string[];
  /** Enlace asociado (p. ej. descarga de reporte listo) */
  url?: string;
  readed?: boolean;
}

export interface NotificationDto
  extends Omit<Notification, "id" | "createdAt" | "updatedAt" | "collections"> {
  lang: string;
}

export interface NotificationUpdateDto
  extends Omit<Notification, "createdAt" | "updatedAt" | "collections"> {
  lang: string;
}

export interface NotificationResponse extends Omit<Notification, "addressee"> {}

interface NotificationsStore {
  notifications: Notification[];
  loading: boolean;
  error: string | null;
  meta?: {
    error: string | null;
    count: number;
    status: number;
    currentPage: number;
    totalPages: number;
    totalItems: number;
    url: string;
  };
  fetchNotifications: (params?: {
    limit?: number;
    page?: number;
    offset?: number;
  }) => Promise<void>;
  updateNotificationInStore: (
    notificationId: string,
    updatedNotification: Partial<Notification>
  ) => void;
  markNotificationAsRead: (notificationId: string) => Promise<void>;
  setNotifications: (
    notifications: Notification[],
    meta?: {
      error: string | null;
      count: number;
      status: number;
      currentPage: number;
      totalPages: number;
      totalItems: number;
      url: string;
    }
  ) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
}

export const useNotificationsStore = create<NotificationsStore>((set, get) => ({
  notifications: [],
  loading: false,
  error: null,
  meta: undefined,

  fetchNotifications: async (params = {}) => {
    if (get().loading) return;
    const fetchGen = getAdminFetchGeneration();
    set({ loading: true, error: null });
    
    try {
      const { data } = await api.get("/notificationsList", { params });
      if (getAdminFetchGeneration() !== fetchGen) return;
      set({ notifications: data.data || [], meta: data.meta, loading: false });
    } catch (err: any) {
      if (getAdminFetchGeneration() !== fetchGen || isAbortError(err)) return;
      set({ error: getError(err), loading: false });
    }
  },

  updateNotificationInStore: (
    notificationId: string,
    updatedNotification: Partial<Notification>
  ) => {
    set((state) => ({
      notifications: state.notifications.map((notification) =>
        notification.id === notificationId
          ? { ...notification, ...updatedNotification }
          : notification
      ),
    }));
  },

  markNotificationAsRead: async (notificationId: string) => {
    try {
      await api.put("/notifications-read", { notification_id: notificationId });
      get().updateNotificationInStore(notificationId, { readed: true });
    } catch (err) {
      if (!isAbortError(err)) console.warn("Error marking notification as read:", err);
    }
  },

  setNotifications: (notifications: Notification[], meta) => {
    set({ notifications, meta });
  },

  setLoading: (loading: boolean) => set({ loading }),

  setError: (error: string | null) => set({ error }),
}));

export const getNotifications = async (
  params: {
    limit?: number;
    page?: number;
    offset?: number;
    status?: string;
    type_of_addressee?: string;
    search?: string;
  },
  t: TFunction<"translation", undefined>,
  showAlert: (config: CustomAlertProps) => void
) => {
  const store = useNotificationsStore.getState();

  store.setLoading(true);
  store.setError(null);

  try {
    const { data } = await api.get("/notificationsList", { params });

    if (data.data) {
      store.setNotifications(data.data, data.meta);
    }
  } catch (error) {
    if (isAbortError(error)) return;

    const errorMessage = getError(error, t);
    store.setError(errorMessage);

    showAlert({
      title: t("notifications.form.errorMessage"),
      text: errorMessage,
      icon: "error",
      confirmButtonText: "OK",
      onConfirm: () => {},
    });
  } finally {
    store.setLoading(false);
  }
};

export const createNotification = async (
  formData: FormData,
  showAlert: (config: CustomAlertProps) => void,
  setLoadingSubmit: (loading: boolean) => void,
  t: TFunction<"translation", undefined>,
  onClose: () => void,
  reset: UseFormReset<NotificationDto>
) => {
  try {
    await apiFormData.post("/notifications", formData);

    setLoadingSubmit(false);
    showAlert({
      title: t("notifications.form.success"),
      text: t("notifications.form.successMessage"),
      icon: "success",
      confirmButtonText: "OK",
      onConfirm: () => {
        onClose();
        reset();
        getNotifications({ limit: 8, page: 1 }, t, showAlert);
      },
      showCancelButton: false,
    });
  } catch (error) {
    setLoadingSubmit(false);
    showAlert({
      title: t("notifications.form.error"),
      text: getError(error, t),
      icon: "error",
      confirmButtonText: "OK",
      onConfirm: () => {},
    });
  }
};

export const updateNotification = async (
  notificationId: string,
  formData: FormData,
  showAlert: (config: CustomAlertProps) => void,
  setLoadingSubmit: (loading: boolean) => void,
  t: TFunction<"translation", undefined>,
  onClose: () => void,
  reset: UseFormReset<NotificationDto>
) => {
  try {
    const { data } = await apiFormData.put("/notifications", formData, {
      params: { notification_id: notificationId },
    });

    if (data.data) {
      const updatedData = { ...data.data.data };
      if (updatedData.image) {
        const separator = updatedData.image.includes("?") ? "&" : "?";
        updatedData.image = `${updatedData.image}${separator}t=${Date.now()}`;
      }
      useNotificationsStore
        .getState()
        .updateNotificationInStore(notificationId, updatedData);
    }

    setLoadingSubmit(false);
    showAlert({
      title: t("notifications.form.updateSuccess"),
      text: t("notifications.form.updateSuccessMessage"),
      icon: "success",
      confirmButtonText: "OK",
      onConfirm: () => {
        onClose();
        reset();
      },
      showCancelButton: false,
    });
  } catch (error) {
    setLoadingSubmit(false);
    showAlert({
      title: t("notifications.form.updateError"),
      text: getError(error, t),
      icon: "error",
      confirmButtonText: "OK",
      onConfirm: () => {},
    });
  }
};

interface DeviceInfo {
  device: string;
  platform: string;
  userAgent: string;
}

export const subscribeToNotification = async (
  id: string,
  tokenCFM: string,
  deviceInfo?: DeviceInfo
) => {
  try {
    const { data } = await api.post("/notifications/suscribe", {
      id,
      tokenCFM,
      device: deviceInfo?.device,
      platform: deviceInfo?.platform,
      userAgent: deviceInfo?.userAgent,
    });
    return data;
  } catch (error) {
    toast.error(getError(error), { id: "fcm-subscribe-error" });
    return null;
  }
};

export const unsubscribeFromNotification = async (
  id: string,
  tokenCFM: string
) => {
  try {
    const { data } = await api.post("/notifications/unsuscribe", {
      id,
      tokenCFM,
    });
    return data;
  } catch (error) {
    if (!isAbortError(error)) console.warn("Error unsubscribing FCM token:", error);
    return null;
  }
};